/**
 * 관리자 > 광고 클릭 통계 — 차트 렌더링 컨트롤러.
 *
 * 라우트 메서드명 'index' 로 디스패치(routes/admin.php: admin.site.ad-fraud.index).
 * #fraud-bootstrap JSON 을 읽어 3개 차트를 그린다:
 *   fraud-trend    → 일자별 클릭/부정 라인
 *   fraud-reason   → 부정 사유 도넛
 *   fraud-campaign → 캠페인별 클릭/부정 막대
 *
 * Chart.js 4.4.x CDN UMD 로드 전제(뷰의 admin_script 섹션).  AdminAnalyticsController 패턴 동일.
 */
import { Logger } from "../../utils/Logger";

declare const Chart: any;

interface TrendBlock { labels: string[]; total: number[]; fraud: number[]; }
interface ReasonRow  { fraud_reason: string; count: number; }
interface CampaignRow { label: string; total: number; fraud: number; }

interface FraudBootstrap {
  trend?:     TrendBlock;
  reasons?:   ReasonRow[];
  campaigns?: CampaignRow[];
}

const CTRL = "AdminAdFraudController";

const PALETTE = [
  "#ef4444", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899",
  "#14b8a6", "#f97316", "#6366f1", "#84cc16", "#06b6d4",
];

const REASON_LABELS: Record<string, string> = {
  same_ip_repeated_clicks:      "동일 IP 반복",
  same_visitor_repeated_clicks: "동일 방문자 반복",
  too_fast_reclick:             "빠른 재클릭",
  empty_user_agent:             "빈 User-Agent",
  detector_error_no_track:      "탐지 오류(차단)",
};

const INK   = "#0a0a0a";
const MUTED = "#737373";
const GRID  = "rgba(0,0,0,0.06)";

function applyDefaults(): void {
  if (typeof Chart === "undefined" || !Chart.defaults) return;
  Chart.defaults.font.family =
    "'Pretendard','Apple SD Gothic Neo',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif";
  Chart.defaults.font.size = 12;
  Chart.defaults.color = MUTED;
  Chart.defaults.plugins.legend.labels.usePointStyle = true;
  Chart.defaults.plugins.legend.labels.boxWidth = 8;
  Chart.defaults.plugins.legend.labels.padding = 14;
  Chart.defaults.plugins.tooltip.backgroundColor = INK;
  Chart.defaults.plugins.tooltip.padding = 10;
  Chart.defaults.plugins.tooltip.cornerRadius = 8;
  Chart.defaults.plugins.tooltip.titleColor = "#fff";
  Chart.defaults.plugins.tooltip.bodyColor = "#e5e5e5";
  if (Chart.defaults.scale?.grid)   Chart.defaults.scale.grid.color   = GRID;
  if (Chart.defaults.scale?.border) Chart.defaults.scale.border.display = false;
}

function readBootstrap(): FraudBootstrap | null {
  const el = document.getElementById("fraud-bootstrap");
  if (!el?.textContent) return null;
  try { return JSON.parse(el.textContent) as FraudBootstrap; }
  catch (e) { Logger.debug(`${CTRL} bootstrap parse fail`); return null; }
}

function ctx(id: string): CanvasRenderingContext2D | null {
  const el = document.getElementById(id) as HTMLCanvasElement | null;
  return el?.getContext("2d") ?? null;
}

function reasonLabel(raw: string): string {
  return String(raw || "—")
    .split(",")
    .map((p) => REASON_LABELS[p.trim()] ?? p.trim())
    .join(" + ");
}

/** 일자별 클릭/부정 라인 */
function renderTrend(report: FraudBootstrap): void {
  const c = ctx("fraud-trend");
  const t = report.trend;
  if (!c || !t || !t.labels?.length) return;
  const base = { tension: 0.35, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12 };
  new Chart(c, {
    type: "line",
    data: {
      labels: t.labels,
      datasets: [
        { ...base, label: "전체 클릭", data: t.total, borderColor: "#3b82f6", backgroundColor: "rgba(59,130,246,0.08)", fill: true },
        { ...base, label: "부정 클릭", data: t.fraud, borderColor: "#ef4444", backgroundColor: "rgba(239,68,68,0.08)", fill: true },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: "top" } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    },
  });
}

/** 부정 사유 도넛 */
function renderReasons(report: FraudBootstrap): void {
  const c = ctx("fraud-reason");
  const rows = report.reasons ?? [];
  if (!c || !rows.length) return;
  new Chart(c, {
    type: "doughnut",
    data: {
      labels: rows.map((r) => reasonLabel(r.fraud_reason)),
      datasets: [{
        data: rows.map((r) => r.count),
        backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]),
        borderWidth: 2, borderColor: "#fff",
      }],
    },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right" } }, cutout: "62%" },
  });
}

/** 캠페인별 클릭/부정 막대 */
function renderCampaigns(report: FraudBootstrap): void {
  const c = ctx("fraud-campaign");
  const rows = report.campaigns ?? [];
  if (!c || !rows.length) return;
  new Chart(c, {
    type: "bar",
    data: {
      labels: rows.map((r) => r.label),
      datasets: [
        { label: "전체 클릭", data: rows.map((r) => r.total), backgroundColor: "#93c5fd" },
        { label: "부정 클릭", data: rows.map((r) => r.fraud), backgroundColor: "#ef4444" },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: "top" } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    },
  });
}

// ── 진입점 ──────────────────────────────────────────────────────────────
export const AdminAdFraudController = {
  /** 광고 클릭 통계 (라우트: index) */
  index(): void {
    Logger.debug(`${CTRL}.index`);
    if (typeof Chart === "undefined") return;
    const report = readBootstrap();
    if (!report) return;
    applyDefaults();
    try { renderTrend(report); }     catch (e) { Logger.debug(`trend: ${(e as Error).message}`); }
    try { renderReasons(report); }   catch (e) { Logger.debug(`reason: ${(e as Error).message}`); }
    try { renderCampaigns(report); } catch (e) { Logger.debug(`campaign: ${(e as Error).message}`); }
  },
};
